<template>
  <div class="flex-container actions--container">
    <template v-if="isMobileViewport">
      <div v-if="showTimer" class="ml-auto">
        <span class="timer">{{ closingWindowText }}</span>
      </div>
      <div v-if="showFreeEntryPointTimer" class="ml-auto">
        <span class="timer">{{ freeEntryPointWindowText }}</span>
      </div>
    </template>
    <div class="action-buttons">
      <lia-header-button
        v-if="hasValidCopilotIntegration"
        :conversation-id="currentChat.id"
        :is-sidebar-open="isLiaSidebarOpen"
        @open-sidebar="$emit('toggle-lia-sidebar')"
      />
      <opportunity-action-button
        v-if="showOpportunityActionButton"
        :opportunity="opportunity"
        @open-create-modal="toggleOpportunityModal"
        @open-sidebar="$emit('toggle-opportunity-sidebar')"
      />
      <CallOutgoingButton
        v-if="showCallOutgoing"
        @openWhatsappTemplate="handleOpenWhatsappTemplate"
      />
      <resolve-action
        v-if="showResolveAction"
        :conversation-id="currentChat.id"
        :status="currentChat.status"
        @open-panel="openPanel"
      />
    </div>
    <email-transcript-modal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailActionsModal"
    />
    <whatsapp-templates-modal
      v-if="showWhatsappTemplatesModal"
      :show="showWhatsappTemplatesModal"
      :inbox-id="currentChat.inbox_id"
      :selected-template="selectedWhatsappTemplate"
      @cancel="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsappTemplate"
    />
    <opportunity-create-modal
      v-if="showOpportunityModal"
      :show="showOpportunityModal"
      :current-chat="currentChat"
      :pipelines="pipelines"
      @close="toggleOpportunityModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';
import EmailTranscriptModal from './EmailTranscriptModal';
import ResolveAction from '../../buttons/ResolveAction';
import CallOutgoingButton from '../../buttons/CallOutgoingButton.vue';
import OpportunityActionButton from '../../buttons/OpportunityActionButton.vue';
import LiaHeaderButton from '../../buttons/LiaHeaderButton.vue';
import OpportunityCreateModal from '../../../routes/dashboard/conversation/OpportunityCreateModal.vue';
import { FEATURE_FLAGS } from '../../../featureFlags';
import agentMixin from 'dashboard/mixins/agentMixin';
import conversationWindowTimersMixin from 'dashboard/mixins/conversationWindowTimersMixin';
import liaCopilotMixin from 'dashboard/mixins/liaCopilotMixin';
import viewportMixin from 'shared/mixins/viewportMixin';
import WhatsappTemplatesModal from './WhatsappTemplates/Modal.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from '../../../routes/dashboard/commands/commandBarBusEvents';

export default {
  components: {
    EmailTranscriptModal,
    ResolveAction,
    CallOutgoingButton,
    OpportunityActionButton,
    LiaHeaderButton,
    OpportunityCreateModal,
    WhatsappTemplatesModal,
  },
  mixins: [
    alertMixin,
    clickaway,
    agentMixin,
    conversationWindowTimersMixin,
    liaCopilotMixin,
    viewportMixin,
  ],
  props: {
    isLiaSidebarOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showEmailActionsModal: false,
      showWhatsappTemplatesModal: false,
      selectedWhatsappTemplate: null,
      showOpportunityModal: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      isObserverMode: 'observerMode/isObserverMode',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      pipelines: 'opportunityPipelines/getRecords',
      opportunity: 'opportunities/getOpportunity',
      getUIFlags: 'opportunities/getUIFlags',
      inboxAllowCalls: 'inboxes/allowCalls',
    }),
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentChat.inbox_id);
    },
    showResolveAction() {
      if (
        this.isObserverMode ||
        (this.inbox.lock_to_single_conversation === false &&
          this.currentChat.status == 'resolved')
      ) {
        return false;
      }
      return true;
    },
    showCallOutgoing() {
      if (
        !this.currentChat || 
        !this.inboxAllowCalls(this.currentChat.inbox_id) ||
        !this.isFeatureEnabledonAccount(this.currentChat.account_id, FEATURE_FLAGS.CALL_OUTGOING)
      ) {
        return false;
      }

      return this.canCallOutgoing;
    },
    showOpportunityActionButton() {
      if (!this.currentChat) return false;
      if (
        !this.isFeatureEnabledonAccount(
          this.currentChat.account_id,
          FEATURE_FLAGS.OPPORTUNITY_PIPELINES
        )
      ) {
        return false;
      }
      if (this.opportunity) return true;
      if (this.currentChat.status !== 'open') return false;
      if (this.getUIFlags?.isFetching) return false;
      if (!this.pipelines || !this.pipelines.length) return false;

      const validPipelines = this.pipelines.some(
        pipeline =>
          pipeline.inbox_ids &&
          pipeline.inbox_ids.includes(this.currentChat.inbox_id) &&
          pipeline.user_ids &&
          pipeline.user_ids.includes(this.currentUser.id) &&
          pipeline.status === 'active'
      );

      return validPipelines;
    },
    canCallOutgoing() {
      const canAgentCallOutgoing = this.agentRules?.hasOwnProperty(
        'call_outgoing'
      )
        ? this.agentRules.call_outgoing === true
        : true;

      return (
        canAgentCallOutgoing ||
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentRole === 'administrator'
      );
    },
  },
  mounted() {
    bus.$on(CMD_MUTE_CONVERSATION, this.mute);
    bus.$on(CMD_UNMUTE_CONVERSATION, this.unmute);
    bus.$on(CMD_SEND_TRANSCRIPT, this.toggleEmailActionsModal);
  },
  beforeDestroy() {
    bus.$off(CMD_MUTE_CONVERSATION, this.mute);
    bus.$off(CMD_UNMUTE_CONVERSATION, this.unmute);
    bus.$off(CMD_SEND_TRANSCRIPT, this.toggleEmailActionsModal);
  },
  destroyed() {
    bus.$off(CMD_MUTE_CONVERSATION, this.mute);
    bus.$off(CMD_UNMUTE_CONVERSATION, this.unmute);
    bus.$off(CMD_SEND_TRANSCRIPT, this.toggleEmailActionsModal);
  },
  methods: {
    mute() {
      this.$store.dispatch('muteConversation', this.currentChat.id);
      this.showAlert(this.$t('CONTACT_PANEL.MUTED_SUCCESS'));
    },
    unmute() {
      this.$store.dispatch('unmuteConversation', this.currentChat.id);
      this.showAlert(this.$t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    },
    toggleEmailActionsModal() {
      this.showEmailActionsModal = !this.showEmailActionsModal;
    },
    handleOpenWhatsappTemplate(template) {
      this.selectedWhatsappTemplate = template;
      this.showWhatsappTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsappTemplatesModal = false;
      this.selectedWhatsappTemplate = null;
    },
    async onSendWhatsappTemplate(messagePayload) {
      try {
        await this.$store.dispatch('createPendingMessageAndSend', {
          conversationId: this.currentChat.id,
          ...messagePayload,
        });
        this.hideWhatsappTemplatesModal();
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    openPanel(options) {
      this.$emit('open-panel', options);
    },
    toggleOpportunityModal() {
      this.showOpportunityModal = !this.showOpportunityModal;
    },
  },
};
</script>

<style scoped lang="scss">
.actions--container {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small);
  justify-content: flex-end;
  position: relative;

  @media screen and (max-width: 768px) {
    justify-content: center;
    margin-top: var(--space-normal);
  }
}

.more--button {
  align-items: center;
  display: flex;
  margin-left: var(--space-small);
}

.timer {
  display: inline-block;
  margin-left: auto;
  margin-right: var(--space-normal);
  color: #5e799b;
  font-size: var(--font-size-small);
  font-weight: 400;
  white-space: normal;
  word-break: break-word;
  max-width: 100%;
}

.action-buttons {
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;

  @media screen and (max-width: 1300px) {
    ::v-deep .btn-call-outgoing {
      min-width: 0;
      order: -1;

      .button__content {
        display: none;
      }
    }
  }

  @media screen and (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;

    ::v-deep .call-button-container {
      order: -1;
    }

    ::v-deep .resolve-actions {
      order: -1;
    }
  }
}

.dropdown-pane {
  right: var(--space-minus-small);
  top: 48px;
}

.icon {
  margin-right: var(--space-smaller);
  min-width: var(--space-normal);
}
</style>
